import { toNDigits } from '@mail/cross-sizes-utils';
import { download } from 'Cloud/Application/Download';
import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { IS_PUBLIC_OF_OVERQUOTA } from 'reactApp/appHelpers/configHelpers';
import { publicOverquota } from 'reactApp/appHelpers/featuresHelpers/features/publicOverquota';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { isVirusItem } from 'reactApp/modules/file/utils';
import { getCurrentPublicItem } from 'reactApp/modules/public/public.selectors';
import type { PublicFile } from 'reactApp/modules/public/public.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getCurrentStockItem } from 'reactApp/modules/stock/stock.selectors';
import type { StockFile } from 'reactApp/modules/stock/stock.type';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { FileType } from 'reactApp/types/FileType';
import { Button } from 'reactApp/ui/Button/Button';
import { EmptyPublicOverquota } from 'reactApp/ui/Empty/new/EmptyPublicOverquota';
import { FileIcon } from 'reactApp/ui/FileIcon/FileIcon';
import { ToolbarNew } from 'reactApp/ui/Toolbar/Toolbar.new';
import { canCloneWeblink } from 'reactApp/utils/cloneHelpers';
import { formatDefaultDate } from 'reactApp/utils/formatDate';

import styles from './File.css';

export const File = memo(function File({ isShove = false }: { isShove?: boolean }): ReactElement {
    const storage = useSelector(getCurrentStorage);
    const isStock = storage === EStorageType.stock;
    const publicItem = useSelector(getCurrentPublicItem) as PublicFile;
    const stockItem = useSelector(getCurrentStockItem) as StockFile;
    const item = isStock ? stockItem : publicItem;
    const mtime = item && 'mtime' in item && item?.mtime;

    const showDisabledForOverquotaOwner = IS_PUBLIC_OF_OVERQUOTA && publicOverquota;

    const size = toNDigits({ bytes: item?.size || 0, maxDigitsCount: 3 }).value;
    const isAnonymous = useSelector(UserSelectors.isAnonymous);
    const canClone = canCloneWeblink(!isAnonymous, storage);

    const onDownload = useCallback(
        () => download(item, undefined, undefined, storage === EStorageType.stock ? undefined : storage, true),
        [item, storage]
    );
    const onClone = useCallback(() => toolbarActions.clone({ id: item?.id, source: 'shoved' }), [item?.id]);

    if (showDisabledForOverquotaOwner && !isStock) {
        return (
            <div className={classNames(styles.wrapper, { [styles.wrapper_shove]: isShove })}>
                <ToolbarNew onDownload={onDownload} />
                <div className={styles.emptyOverquota}>
                    <EmptyPublicOverquota forceDark={!isShove} />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div className={styles.icon}>
                <FileIcon size="l" type={isVirusItem(item) ? FileType.VIRUS : item?.ext} />
            </div>
            <div className={styles.fileName}>{item?.name}</div>
            <div className={styles.description}>
                <span data-name="size">{size}</span>{' '}
                {mtime && (
                    <>
                        , <span data-name="date">{formatDefaultDate(mtime)}</span>
                    </>
                )}
            </div>
            <div className={styles.buttonWrapper}>
                <Button theme="octavius" primary middle data-name="download" onClick={onDownload}>
                    Скачать
                </Button>
                {canClone && (
                    <Button theme="octavius" middle data-name="clone" onClick={onClone}>
                        Сохранить в облако
                    </Button>
                )}
            </div>
        </div>
    );
});
